import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { defaultThemeKey, themeMap, themes, ThemeDefinition, ThemeKey } from './themes';

interface ThemeContextValue {
  theme: ThemeDefinition;
  setTheme: (key: ThemeKey) => void;
  themeOptions: ThemeDefinition[];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'cooptest-theme';

function applyTheme(theme: ThemeDefinition) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(theme.properties).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
  root.setAttribute('data-theme', theme.key);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [currentKey, setCurrentKey] = useState<ThemeKey>(() => {
    if (typeof window === 'undefined') {
      return defaultThemeKey;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY) as ThemeKey | null;
    return stored && themeMap[stored] ? stored : defaultThemeKey;
  });

  const theme = useMemo(() => themeMap[currentKey], [currentKey]);

  useEffect(() => {
    applyTheme(theme);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, theme.key);
    }
  }, [theme]);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    setTheme: setCurrentKey,
    themeOptions: themes,
  }), [theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
